import React from 'react';

import { Link } from 'react-router-dom';

import { type CardSizes } from '@pkg/components';
import { useGetMultichainCollectionMetadata } from '@pkg/hooks';

import { CollectionChainType, links, SupportedTokens } from '@app/client';
import { CollectionLink } from '@app/pages/Collection/components/CollectionLink';
import { HoverUnderline } from '@app/pages/NFTDetailView/components/MainStage/components/NFTInfo/styles';

import { CollectionName, Info, Inside, NftName, Thumbnail, ThumbnailSkeleton, Wrapper } from './styles';

interface CardNameProps {
    name?: string | null;
    collection_name?: string | null;
    collection_image?: string | null;
    collection_slug?: string | null;
    contract?: string | null;
    token_id?: string | null;
    cardSize?: CardSizes;
    hideCollectionData?: boolean;
    chain: CollectionChainType;
}

const getSlugQuery = (chain: CollectionChainType, slug?: string | null) => {
    if (!slug) return;
    return slug.startsWith(chain.toLowerCase()) ? slug : `${chain.toLowerCase()}.${slug}`;
};

export const CardName = ({
    name,
    hideCollectionData,
    collection_name,
    collection_image,
    collection_slug,
    contract,
    token_id,
    cardSize,
    chain = CollectionChainType.Ethereum,
}: CardNameProps) => {
    const {
        data: collectionDataResponse,
        isLoading,
        isError,
    } = useGetMultichainCollectionMetadata({
        slugQuery: [getSlugQuery(chain, collection_slug || contract) || ''],
        disabled: !contract,
        type: 'contract',
    });

    const collectionData = collectionDataResponse?.[chain.toLowerCase()];

    const _collection_name = collection_name || collectionData?.[0]?.name || '';
    const _collection_slug = collection_slug || collectionData?.[0]?.slugs?.[0] || undefined;
    const _collection_image =
        collection_image ||
        collectionData?.image ||
        links.daas().v2.assets.collections(getSlugQuery(chain, _collection_slug) || '');

    return (
        <Wrapper $cardSize={cardSize}>
            <Inside $setHeight={!hideCollectionData} $cardSize={cardSize}>
                {!hideCollectionData &&
                    !_collection_image &&
                    !!contract &&
                    !isError &&
                    isLoading &&
                    cardSize !== 'small' && <ThumbnailSkeleton />}

                {!hideCollectionData && _collection_image && cardSize !== 'small' && (
                    <CollectionLink slug={_collection_slug} chain={chain}>
                        <Thumbnail title={_collection_name || ''} $image={_collection_image} />
                    </CollectionLink>
                )}

                <Info>
                    {!hideCollectionData && _collection_name ? (
                        <CollectionName title={_collection_name} $cardSize={cardSize}>
                            <CollectionLink slug={_collection_slug} chain={chain}>
                                {_collection_name}
                                <HoverUnderline />
                            </CollectionLink>
                        </CollectionName>
                    ) : null}

                    {/* show NftName using CollectionName style */}
                    {(!_collection_name && name) || hideCollectionData ? (
                        <CollectionName title={name || ''} $cardSize={cardSize}>
                            {chain ? (
                                <Link to={`/collectible/${SupportedTokens[chain]}/${contract}/${token_id}?goBack=1`}>
                                    {name} <HoverUnderline />
                                </Link>
                            ) : (
                                name
                            )}
                        </CollectionName>
                    ) : null}

                    {_collection_name && name && !hideCollectionData ? (
                        <NftName title={name} $cardSize={cardSize}>
                            {chain ? (
                                <Link to={`/collectible/${SupportedTokens[chain]}/${contract}/${token_id}?goBack=1`}>
                                    {name}
                                </Link>
                            ) : (
                                name
                            )}
                        </NftName>
                    ) : null}
                </Info>
            </Inside>
        </Wrapper>
    );
};
